import { useState, useEffect } from "react";

import {
  getEmptyFieldsErrorFromTemplate,
  getDefaultFormFromTemplate,
} from "../../utils/form";

import {
  getUserName,
  checkIfValueIsEmpty,
  isValidEmailAddress,
} from "../../utils/utils";
import { adminEndpoints } from "../../api/endpoints";

const getClientLocationAssignmentFormTemplate = (location) => [
  {
    type: "input_number",
    key: "client_id",
    name: "client_id",
    width: "50%",
    hide: true,
    default: null,
  },
  {
    type: "input_number",
    key: "location_id",
    name: "location_id",
    width: "50%",
    optional: true,
    hide: true,
    default: null,
  },
  {
    type: "address",
    key: "street_and_number",
    name: "Straße Nr.",
    width: "50%",
    disabled: !!location,
    selectAddress: (addr, form, setForm) =>
      setForm((f) => ({
        ...f,
        street_and_number: addr.street_and_number,
        zip_and_city: addr.zip_and_city,
      })),
    default: "",
  },
  {
    type: "input_chars",
    key: "zip_and_city",
    name: "PLZ Ort",
    disabled: true,
    width: "50%",
    default: "",
  },
  {
    type: "input_chars",
    key: "name",
    name: "Kunde",
    disabled: !!location,
    width: "50%",
    optional: true,
    default: "",
  },
];

const defaultClientLocationAssignmentForm = getDefaultFormFromTemplate(
  getClientLocationAssignmentFormTemplate(null),
);

const getUserFormTemplate = () => [
  {
    type: "input_chars",
    key: "username",
    name: "Benutzername",
    width: "50%",
    disabled: true,
    default: "",
  },
  {
    type: "input_chars",
    key: "first_name",
    name: "Vorname",
    width: "50%",
    default: "",
  },
  {
    type: "input_chars",
    key: "last_name",
    name: "Nachname",
    width: "50%",
    default: "",
  },
  {
    type: "input_chars",
    key: "email",
    name: "E-Mail",
    width: "50%",
    isEmpty: (value) =>
      checkIfValueIsEmpty(value) || !isValidEmailAddress(value),
    default: "",
  },
];

const defaultUserForm = getDefaultFormFromTemplate(getUserFormTemplate());

export default function useClientLocation(
  client,
  resetParent,
  location = null,
  apiClient,
  showMsg,
  isFocused = true,
) {
  const [locationAssignmentForm, setLocationAssignmentForm] = useState({
    ...defaultClientLocationAssignmentForm,
  });
  const [createUser, setCreateUser] = useState(false);
  const [userForm, setUserForm] = useState({ ...defaultUserForm });
  const [showMissingFields, setShowMissingFields] = useState(false);

  useEffect(() => {
    setUserForm((f) => ({
      ...f,
      username: getUserName(f.first_name, f.last_name),
    }));
  }, [userForm.first_name, userForm.last_name]);

  const clearData = () => {
    setLocationAssignmentForm({ ...defaultClientLocationAssignmentForm });
    setUserForm({ ...defaultUserForm });
    setCreateUser(false);
    setShowMissingFields(false);
  };

  const loadData = () => {
    setLocationAssignmentForm({
      ...defaultClientLocationAssignmentForm,
      client_id: client.id,
      location_id: location?.id || null,
      street_and_number: location?.street_and_number || "",
      zip_and_city: location?.zip_and_city || "",
      name: location?.name || "",
    });
  };

  const initData = loadData;

  useEffect(() => {
    if (isFocused) initData();
    else clearData();
  }, [isFocused]);

  const submit = async (assignmentForm, userForm) => {
    const response = await apiClient.post(
      adminEndpoints.assignclient,
      assignmentForm,
    );
    if (userForm) {
      userForm = { ...userForm, location: response.data.id };
      await apiClient.post(adminEndpoints.customer, userForm);
    }
  };

  const onSubmit = async (onSuccess) => {
    const emptyLocationfieldsError = getEmptyFieldsErrorFromTemplate(
      locationAssignmentTemplate,
      locationAssignmentForm,
    );
    const emptyUserfieldsError = createUser
      ? getEmptyFieldsErrorFromTemplate(userTemplate, userForm)
      : null;
    if (emptyLocationfieldsError || emptyUserfieldsError) {
      setShowMissingFields(true);
      showMsg?.("Bitte alle Informationen eintragen!", "warning");
      console.warn(emptyLocationfieldsError || emptyUserfieldsError);
      return;
    }

    try {
      await submit(locationAssignmentForm, createUser ? userForm : null);
      showMsg?.("Das Gerät wurde erfolgreich zugeordnet.");
      resetParent?.();
      onSuccess?.();
    } catch (error) {
      console.warn('Error in "client:onSubmit"', error, error.stack);
      showMsg?.("Das Gerät konnte nicht zugeordnet werden.", "error");
    }
  };

  const userTemplate = getUserFormTemplate();
  const locationAssignmentTemplate =
    getClientLocationAssignmentFormTemplate(location);

  return {
    createUser,
    userForm: {
      template: userTemplate,
      form: userForm,
      setForm: setUserForm,
      showMissingFields,
    },
    locationAssignmentForm: {
      template: locationAssignmentTemplate,
      form: locationAssignmentForm,
      setForm: setLocationAssignmentForm,
      showMissingFields,
    },
    onSubmit,
    data: { update: loadData, clear: clearData, init: initData },
  };
}
